
import { 
  Shield,
  Users, 
  AlertTriangle, 
  Eye,
  TrendingUp,
  BookOpen
} from "lucide-react";

interface ServiceCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
}

const ServiceCard = ({ title, description, icon }: ServiceCardProps) => {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-md hover:border-purple-100 h-full flex flex-col">
      <div className="p-3 rounded-lg bg-purple-50 w-fit mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-3 text-gray-900">{title}</h3>
      <p className="text-gray-600 flex-grow">{description}</p>
    </div>
  );
};

export const ServiceCards = () => {
  const services = [
    {
      title: "Phishing Simulations",
      description: "Realistic phishing campaigns that test employee awareness and provide immediate education on security best practices.",
      icon: <AlertTriangle className="w-6 h-6 text-edgefi-purple" />
    },
    {
      title: "Security Awareness Training",
      description: "Engaging, interactive training modules that build cybersecurity knowledge and encourage secure behaviors.",
      icon: <Users className="w-6 h-6 text-edgefi-purple" />
    },
    {
      title: "Security Culture Development",
      description: "Strategic initiatives to foster a security-minded culture where everyone takes responsibility for protection.",
      icon: <Shield className="w-6 h-6 text-edgefi-purple" />
    },
    {
      title: "Threat Recognition Training",
      description: "Specialized training to help employees identify and respond to social engineering, malware, and emerging threats.",
      icon: <Eye className="w-6 h-6 text-edgefi-purple" />
    },
    {
      title: "Progress Measurement",
      description: "Comprehensive reporting and metrics to track improvements in security awareness and reduction in security incidents.",
      icon: <TrendingUp className="w-6 h-6 text-edgefi-purple" />
    },
    {
      title: "Compliance Training",
      description: "Industry-specific training modules designed to meet regulatory requirements and compliance standards.",
      icon: <BookOpen className="w-6 h-6 text-edgefi-purple" />
    }
  ];

  return (
    <section className="py-16 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Comprehensive Awareness Services</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Our security awareness training helps organizations build a human firewall, transforming employees from potential vulnerabilities into your strongest defense.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <ServiceCard 
              key={index}
              title={service.title}
              description={service.description}
              icon={service.icon}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
